import { useRef, useEffect, useState, type FC } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  StackDivider,
  HStack,
  IconButton,
  Drawer,
  useDisclosure,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  VStack,
  DrawerHeader,
  Box,
  Grid,
  GridItem,
  Link,
  Progress,
} from "@chakra-ui/react";
import { MdMenu, MdOutlineShoppingCart } from "react-icons/md";
import {
  FacebookProfile,
  InstagramProfile,
} from "~/components/SocialMediaButtons";
import { useGlobalNavigationState } from "remix-utils/use-global-navigation-state";
import Logo from "./Logo";

const MainMenuLinks = [
  <Link fontSize={"sm"} as={RouterLink} key={"sklep"} to="/#sklep">
    Sklep
  </Link>,
  <Link fontSize={"sm"} as={RouterLink} key={"onas"} to="/#o-nas">
    O&nbsp;nas
  </Link>,
  <Link fontSize={"sm"} as={RouterLink} key={"kontakt"} to="/#kontakt">
    Kontakt
  </Link>,
];

const additionalMobileLinks = [
  <Link
    fontSize={"sm"}
    as={RouterLink}
    key={"regulamin-sklepu"}
    to="/regulamin"
  >
    Regulamin&nbsp;sklepu
  </Link>,
  <Link
    fontSize={"sm"}
    as={RouterLink}
    key={"polityka-prywatnosci"}
    to="/polityka-prywatnosci"
  >
    Polityka&nbsp;prywatności
  </Link>,
  <Link fontSize={"sm"} as={RouterLink} key={"dostawa"} to="/dostawa">
    Dostawa
  </Link>,
];

const DesktopMenu = () => {
  return (
    <>
      <GridItem justifySelf={"left"}>
        <HStack>
          <Link as={RouterLink} to="/" aria-label="Idź do strony głównej">
            <Logo logoColor={"black"} width={{ base: 150, md: 200 }} />
          </Link>
          <HStack spacing={10} ml={10} fontWeight={"bold"}>
            {MainMenuLinks}
          </HStack>
        </HStack>
      </GridItem>
      <GridItem justifySelf={"center"}></GridItem>
      <GridItem justifySelf={"right"} flexGrow={1}>
        <HStack justifyContent={"end"} alignContent={"right"} spacing={4}>
          <FacebookProfile size={26} />
          <InstagramProfile size={26} />
          <IconButton
            as={RouterLink}
            to="https://koszyk.dystans.cc/cart/new"
            ml={6}
            bgColor={"creamy"}
            _hover={{ color: "white", bgColor: "primary.100" }}
            color="black"
            aria-label="Koszyk zakupowy"
            icon={<MdOutlineShoppingCart size={28} />}
          />
        </HStack>
      </GridItem>
    </>
  );
};

const MobileMenu = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const btnRef = useRef(null);
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
  }, [location]);
  return (
    <>
      <GridItem justifySelf={"left"}>
        <IconButton
          colorScheme="primary"
          ref={btnRef}
          aria-label="Page menu"
          icon={<MdMenu />}
          onClick={onOpen}
        />
        <Drawer
          isOpen={isOpen}
          placement="right"
          onClose={onClose}
          finalFocusRef={btnRef}
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerHeader>
              <Logo logoColor={"black"} width={100} />
              <DrawerCloseButton mt={2} py={0} />
            </DrawerHeader>
            <DrawerBody>
              <VStack
                divider={<StackDivider borderColor="gray.200" />}
                spacing={4}
              >
                {MainMenuLinks}
                {additionalMobileLinks}
                <HStack justifyContent={"end"} spacing={4}>
                  <FacebookProfile size={26} />
                  <InstagramProfile size={26} />
                </HStack>
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </GridItem>
      <GridItem justifySelf={"center"}>
        <Link as={RouterLink} to="/" aria-label="Idź do strony głównej">
          <Logo logoColor={"black"} width={{ base: 150, md: 200 }} />
        </Link>
      </GridItem>
      <GridItem justifySelf={"right"}>
        <IconButton
          as={RouterLink}
          to="https://koszyk.dystans.cc/cart/new"
          bgColor={"creamy"}
          _hover={{ color: "white", bgColor: "primary.100" }}
          color="black"
          aria-label="Koszyk zakupowy"
          icon={<MdOutlineShoppingCart size={28} />}
        />
      </GridItem>
    </>
  );
};

const Header: FC<{ mb: number }> = (props) => {
  const headerRef = useRef(null);
  const [headerIsSticked, setHeaderIsSticked] = useState<boolean>(false);
  const states = useGlobalNavigationState();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (states.includes("loading") || states.includes("submitting")) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [states]);

  useEffect(() => {
    if (headerRef.current) {
      const observer = new IntersectionObserver(
        ([e]) => {
          if (e.intersectionRatio < 1) {
            setHeaderIsSticked(true);
          } else {
            setHeaderIsSticked(false);
          }
        },
        { threshold: [1] }
      );
      observer.observe(headerRef.current);
    }
  }, [headerRef]);

  return (
    <>
      <Progress
        aria-label="Łdowanie strony"
        isIndeterminate={isLoading}
        bg={"none"}
        zIndex={2000}
        position={"fixed"}
        top={0}
        left={0}
        colorScheme="primary"
        width={"100%"}
        size={"sm"}
      />
      <Box
        as="nav"
        mt={1}
        sx={{ position: "sticky", top: "-10px", zIndex: 1000 }}
        borderBottom={headerIsSticked ? "solid 2px" : "none"}
        {...props}
        ref={headerRef}
        py={4}
        w={"100%"}
      >
        <Box
          bgColor={"white"}
          top={0}
          height="100%"
          width="100vw"
          position="absolute"
          left={"50%"}
          ml={"-50vw"}
          maxWidth={"100vw"}
          zIndex={-1}
        />
        <Grid
          templateColumns={{ base: "repeat(3, 1fr)" }}
          alignItems="center"
          justifyItems={"center"}
          display={{ base: "none", md: "grid" }}
        >
          <DesktopMenu />
        </Grid>
        <Grid
          templateColumns={{ base: "repeat(3, 1fr)" }}
          alignItems="center"
          justifyItems={"center"}
          display={{ base: "grid", md: "none" }}
        >
          <MobileMenu />
        </Grid>
      </Box>
    </>
  );
};

export default Header;
